import { useNavigate } from 'react-router-dom'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import {
  AccountsListPageDocument,
  BankingBankAccountFragment,
  FinishBankAccountDocument,
} from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { getDefaultAlias } from 'modules/banking/utils'

import { WizardData } from './types'
import { FinishSetupAliasWizardStepF0 } from './wizardSteps/FinishSetupAliasWizardStepF0'
import { FinishSetupLedgerAccountWizardStepF0 } from './wizardSteps/FinishSetupLedgerAccountWizardStepF0'
import { FinishSetupMembershipsWizardStepF0 } from './wizardSteps/FinishSetupMembershipsWizardStepF0'

interface FinishSetupAccountModalF0Props {
  companyId: string
  bankAccount: BankingBankAccountFragment
  legalEntityId: string
  ledgerAccountsFeatureAvailable: boolean
  accountId?: string
  onClose: () => void
  onFinish: () => void
}

export const FinishSetupAccountModalF0 = ({
  onClose,
  onFinish,
  companyId,
  bankAccount,
  legalEntityId,
  ledgerAccountsFeatureAvailable,
  accountId,
}: FinishSetupAccountModalF0Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const handleClose = (id: string) => {
    navigate(navigation.finance.banking.accounts.accountId.fullPath({ id }))
  }

  const [finishSetup] = useMutation(FinishBankAccountDocument, {
    extractorFn: (data) => data.bankingMutations.finishSetupBankingBankAccount,
    refetchQueries: [AccountsListPageDocument],
  })

  const finishSetupAliasWizardStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_1.side_title'),
      isCompleted: () => true,
      nextText: i18n.t('banking.accounts.finish_wizard.next'),
      render: (props) => <FinishSetupAliasWizardStepF0 {...props} bankAccount={bankAccount} />,
    }),
    [bankAccount]
  )

  const finishSetupLedgerAccountWizardStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_2.side_title'),
      isCompleted: () => true,
      nextText: i18n.t('banking.accounts.finish_wizard.next'),
      render: (props) => (
        <FinishSetupLedgerAccountWizardStepF0
          {...props}
          bankAccount={bankAccount}
          legalEntityId={legalEntityId}
          ledgerAccountsFeatureAvailable={ledgerAccountsFeatureAvailable}
        />
      ),
    }),
    [bankAccount, legalEntityId, ledgerAccountsFeatureAvailable]
  )

  const finishSetupMembershipsWizardStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_3.side_title'),
      isCompleted: () => true,
      nextText: i18n.t('banking.accounts.finish_wizard.finish_account'),
      render: (props) => <FinishSetupMembershipsWizardStepF0 {...props} accountId={accountId} />,
    }),
    [accountId]
  )

  const steps: WizardStep<WizardData>[] = useUnsafeMemo(() => {
    return [
      finishSetupAliasWizardStep,
      finishSetupLedgerAccountWizardStep,
      finishSetupMembershipsWizardStep,
    ].filter((n) => n)
  }, [
    finishSetupAliasWizardStep,
    finishSetupLedgerAccountWizardStep,
    finishSetupMembershipsWizardStep,
  ])

  const initialData: WizardData = useUnsafeMemo(
    () => ({
      ledgerAccountId: undefined,
      accountAlias: bankAccount.accountAlias ?? getDefaultAlias(),
    }),
    [bankAccount.accountAlias]
  )

  const handleSubmit = async (data: WizardData) => {
    const hasEmployees =
      (data.employeeSelection?.type === 'manual' && data.employeeSelection.ids.length > 0) ||
      data.employeeSelection?.type === 'all'

    const result = await finishSetup({
      variables: {
        bankAccountId: bankAccount.id,
        companyId,
        ledgerAccountId: data.ledgerAccountId,
        accountAlias: data.accountAlias,
        employeeSelection: hasEmployees ? data.employeeSelection : undefined,
      },
    }).then((res) => {
      if (hasEmployees) track(trackingEvents.accounting.banking.addPeopleBankAccount)
      return res
    })

    if (result) {
      handleClose(bankAccount.id)
      onFinish()
    }
  }

  return (
    <>
      <Wizard.Modal
        title={i18n.t('banking.accounts.finish_wizard.title')}
        icon="add-block"
        description={i18n.t('banking.accounts.finish_wizard.description')}
        initialData={initialData}
        onSubmit={handleSubmit}
        onClose={onClose}
        steps={steps}
      />
    </>
  )
}
